import { API_ENDPOINT } from "@/constants/appConstants";
import { useProfile } from "@/context/useProfileProps";
import { handleFocusOnField } from "@/helpers/helper";
import { FC, useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";

import { MultiDropDownController } from "@/components/common/dropdownController";
import ProfileModal from "@/components/common/profileModal";

import { useSkill } from "./query";
import {
  DropdownOptionType,
  EditCardType,
  Skill2,
  SuggestedSkillType,
} from "./type";

const EditCard: FC<EditCardType> = (props) => {
  const {
    prefilledData,
    closeEdit,
    isCalledFromOutside = false,
    formRef,
    handleFooterBtnLoading,
    isAutoUpdated = false,
    focusOnField = "",
  } = props;
  const { showToast, handleEventTracking, translation, isMobile } =
    useProfile();
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const { skill: skillContent, errorMessages } = translation;

  const prefilledSkillsData = prefilledData.map((skill: Skill2) => {
    return {
      id: skill?.id ?? "",
      text: skill?.skill?.text,
    };
  });
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);

  const defaultValues: FieldValues = { skills: prefilledSkillsData || [] };
  const { mutate } = useSkill();

  const [selectedSkills, setSelectedSkills] = useState<DropdownOptionType[]>(
    prefilledSkillsData as DropdownOptionType[],
  );
  const [suggestedSkills, setSuggestedSkills] =
    useState<SuggestedSkillType | null>(null);
  const [newSkills, setNewSkills] = useState<DropdownOptionType[] | null>(null);

  useEffect(() => {
    if ((selectedSkills || []).length > 0) {
      suggestedOptions(selectedSkills);
    } else {
      setSuggestedSkills(null);
    }
  }, [selectedSkills]);

  useEffect(() => {
    handleFocusOnField(focusOnField);
  }, [focusOnField]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
  } = useForm<FieldValues>({ defaultValues });

  const handleSaveBtnLoading = (val: boolean) => {
    setIsSaveLoading(val);
    handleFooterBtnLoading && handleFooterBtnLoading({ isSaveLoading: val });
  };

  const handleAddSkills = (skills: DropdownOptionType[]) => {
    const transformedData = skills?.map((item: any) => ({
      skill: {
        text: item.text,
      },
    }));

    mutate(
      { data: transformedData, action: "add" },
      {
        onSuccess: () => {
          showToast({
            show: true,
            message: "Skills updated successfully",
            type: "success",
          });
          let urlParams = new URLSearchParams(window.location.search);
          const isApp = urlParams.get("app") === "true";
          handleEventTracking({
            eventType: "eventPush",
            eventName: "ProfilePage_Section_Saved",
            eventProps: {
              Skills_Saved: JSON.stringify({ transformedData }),
              Source: isApp ? "app" : "web",
            },
          });
          handleSaveBtnLoading(false);
          closeEdit("save");
        },
        onError: (err) => {
          handleSaveBtnLoading(false);
          showToast({
            show: true,
            type: "error",
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
          });
        },
      },
    );
  };

  const handleDeleteSkills = (
    ids: (string | number)[],
    callback?: () => void,
  ) => {
    mutate(
      { data: { ids }, action: "delete" },
      {
        onSuccess: () => {
          if (callback) {
            callback();
          } else {
            showToast({
              show: true,
              message: "Skills updated successfully",
              type: "success",
            });
            let urlParams = new URLSearchParams(window.location.search);
            const isApp = urlParams.get("app") === "true";
            handleEventTracking({
              eventType: "eventPush",
              eventName: "ProfilePage_Section_Saved",
              eventProps: {
                Skills_Saved: JSON.stringify({
                  data: { ids },
                }),
                Source: isApp ? "app" : "web",
              },
            });
            handleSaveBtnLoading(false);
            closeEdit("delete");
          }
        },
        onError: (err) => {
          handleSaveBtnLoading(false);
          showToast({
            show: true,
            type: "error",
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
          });
        },
      },
    );
  };

  const onSubmit = (updatedSkills: any) => {
    if (isAutoUpdated) {
      handleSaveBtnLoading(true);
      if (updatedSkills?.skills) {
        handleAddSkills(updatedSkills?.skills);
      }
      return;
    }

    if (selectedSkills?.length === 0) {
      showToast({
        show: true,
        type: "error",
        message: "Please select a skill",
      });
      return;
    }

    const newText = (updatedSkills?.skills as DropdownOptionType[])?.map(
      (item) => item.text.toLocaleLowerCase(),
    );
    const deletedSkills = prefilledSkillsData?.filter(
      (skill) => !newText.includes(skill.text.toLocaleLowerCase()),
    );

    if ((deletedSkills as DropdownOptionType[])?.length > 0) {
      handleSaveBtnLoading(true);
      const idCollection = deletedSkills?.map((item) => item.id);

      if (newSkills && (newSkills as DropdownOptionType[])?.length > 0) {
        handleDeleteSkills(idCollection, () => handleAddSkills(newSkills));
      } else {
        handleDeleteSkills(idCollection);
      }

      return;
    }

    if (newSkills && (newSkills as DropdownOptionType[])?.length > 0) {
      handleSaveBtnLoading(true);
      handleAddSkills(newSkills);
      return;
    }

    closeEdit("save");
  };

  const trackAddedData = (skills: any) => {
    const existingIds = prefilledSkillsData?.map((item) => item.id);
    const newItems = (skills as DropdownOptionType[])?.filter(
      (item) => !existingIds?.includes(item.id as string),
    );
    if (newItems?.length > 0) {
      setNewSkills(newItems);
    }
    setSelectedSkills(skills);
  };

  const suggestedOptions = async (skills: DropdownOptionType[]) => {
    let queryParam = "";
    if (Array.isArray(skills) && skills.length) {
      queryParam += `&query=${skills[skills.length - 1].text}`;
      skills.forEach((val) => (queryParam += `&excludeKeywords=${val.text}`));
    }
    setIsSuggestionsLoading(true);
    const url = `${API_ENDPOINT(window.location.host).ravenApi.suggestedKeywords}?appName=srp-ui&size=5${queryParam}`;
    const response = await fetch(url);
    const result = await response.json();
    setIsSuggestionsLoading(false);
    const list = result.data;
    const updatedList = (list || [])?.map((item: any) => {
      return {
        text: item?.name,
        id: item?.id,
      };
    });
    setSuggestedSkills({
      list: updatedList,
      type: "Suggested Skills",
    });
  };

  const renderForm = (
    <form
      className="flex h-full flex-col gap-6"
      onSubmit={handleSubmit(onSubmit)}
      ref={formRef}
    >
      <div className="mb-52">
        <MultiDropDownController
          name="skills"
          maxSelect={50}
          control={control}
          isLoading={isSuggestionsLoading}
          isSearchable={true}
          type="SKILL"
          onSelect={(value: DropdownOptionType) => {
            clearErrors("skills");
            setValue("skills", value);
            trackAddedData(value);
          }}
          placeholder={skillContent.skillPlaceholder}
          errorMessage={errors.skills ? errorMessages.fieldRequired : ""}
          {...(suggestedSkills?.list?.length && {
            suggestedOptions: suggestedSkills,
          })}
          rawText={true}
        />
      </div>
    </form>
  );

  const areSkillsSame = (
    skills1: DropdownOptionType[],
    skills2: DropdownOptionType[],
  ) => {
    if (skills1.length !== skills2.length) return false;
    const text1 = skills1
      .map((skill) => skill.text.toLocaleLowerCase())
      .sort((a, b) => a.localeCompare(b));
    const text2 = skills2
      .map((skill) => skill.text.toLocaleLowerCase())
      .sort((a, b) => a.localeCompare(b));
    return text1.every((text, index) => text === text2[index]);
  };

  const [isSameSkills, setIsSameSkills] = useState(() =>
    areSkillsSame(prefilledSkillsData, selectedSkills),
  );
  useEffect(() => {
    const areSkillsSameResult = areSkillsSame(
      prefilledSkillsData,
      selectedSkills,
    );
    setIsSameSkills(areSkillsSameResult);
  }, [prefilledSkillsData, selectedSkills]);

  return !isCalledFromOutside ? (
    <ProfileModal
      id="skills-modal"
      showModal={true}
      closeModal={closeEdit}
      isMobile={isMobile}
      showCloseButton={true}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      showHandleBar={isMobile}
      heading={skillContent.title}
      footerPrimaryBtnLabel="Save"
      footerPrimaryBtnHandler={() => formRef.current?.requestSubmit()}
      footerPrimaryBtnIsLoading={isSaveLoading}
      footerSecondaryBtnIsLoading={isSameSkills}
    >
      {renderForm}
    </ProfileModal>
  ) : (
    renderForm
  );
};

export default EditCard;
